/**
 * Life4Earth V2 — Shared Pre-Flight Data Loader Hook
 *
 * Lazily fetches preflight items from the API for a given project,
 * hydrates the centralized store, and falls back to seed data
 * when the API is unreachable.
 *
 * Designed for use by any tab/panel that displays preflight data
 * (Work → Resources sub-tab, Impact tab, etc.).
 *
 * Call with `enabled = true` only when the consuming panel is
 * actually visible, so the API call is deferred until needed.
 */

import { useEffect, useRef, useState } from 'react';
import {
  fetchPreflightItemsByProject,
  apiResponseToPreflightItem,
} from '../api/preflightItems';
import { preflightStore, storeKey } from '../Data/store';
import { preflightSeedData } from '../Data/PreFlight/preflightSeedData';

/**
 * @param projectGuid  The project to load items for.
 * @param enabled      Gate flag — set to `true` when the panel that
 *                     needs preflight data is visible. When `false`,
 *                     no fetch is issued.
 * @returns            A version counter that increments each time
 *                     the store is updated, so consumers can re-read.
 */
export function usePreflightLoader(
  projectGuid: string,
  enabled: boolean
): { preflightVersion: number; bumpVersion: () => void } {
  const [preflightVersion, setPreflightVersion] = useState(0);
  const apiLoadedForRef = useRef<string>('');

  useEffect(() => {
    if (!enabled) return;
    if (!projectGuid) return;
    if (apiLoadedForRef.current.toUpperCase() === projectGuid.toUpperCase()) return;

    let cancelled = false;
    apiLoadedForRef.current = projectGuid;

    (async () => {
      try {
        console.info(`[PreFlight] Loading items for project ${projectGuid}…`);
        const apiItems = await fetchPreflightItemsByProject(projectGuid);
        if (cancelled) return;

        console.info(`[PreFlight] API returned ${apiItems.length} item(s)`);
        const items = apiItems.map(apiResponseToPreflightItem);
        const key = storeKey(projectGuid);

        if (items.length > 0) {
          preflightStore.set(key, items);
          setPreflightVersion((v) => v + 1);
          return;
        }
      } catch (err: any) {
        console.error('[PreFlight] API fetch failed:', err.message);
        // API unreachable — fall through to seed data
      }

      if (cancelled) return;

      console.info('[PreFlight] Falling back to seed data');
      const fromSeed = preflightSeedData.filter(
        (i) => i.project_guid.toUpperCase() === projectGuid.toUpperCase()
      );
      if (fromSeed.length > 0) {
        const key = storeKey(projectGuid);
        preflightStore.set(key, fromSeed);
        setPreflightVersion((v) => v + 1);
      } else {
        console.warn('[PreFlight] No seed data found for project', projectGuid);
      }
    })();

    return () => {
      cancelled = true;
      // Reset so the next mount (including React 18 strict-mode remount) re-fetches
      apiLoadedForRef.current = '';
    };
  }, [projectGuid, enabled]);

  /* Listen for external preflight changes (e.g. from other tabs or SignalR) */
  useEffect(() => {
    const handler = () => setPreflightVersion((v) => v + 1);
    window.addEventListener('l4e_preflight_changed', handler);
    return () => window.removeEventListener('l4e_preflight_changed', handler);
  }, []);

  return {
    preflightVersion,
    bumpVersion: () => setPreflightVersion((v) => v + 1),
  };
}
